import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import ChatHeader from '../components/ChatHeader';
import ChatMessage from '../components/ChatMessage';
import MessageInput from '../components/MessageInput';
import { useAuth } from '../context/AuthContext';
import { sendMessage, getMessages, getConversations } from '../api/message';
import { getJobById } from '../api/job';
import { Message as ApiMessage, ThreadSummary } from '../types/user';
import { Job } from '../types/job';
import { startSignalRConnection, stopSignalRConnection } from '../utils/signalRConnection';
import QuotePanel from '../components/QuotePanel';

function formatTimeAgo(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

interface ActiveThread {
  jobId: number;
  contractorProfileId: number;
}

const MessagesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // URL params when arriving from a job page
  const urlJobId = searchParams.get('jobId') ? parseInt(searchParams.get('jobId')!) : undefined;
  const urlContractorProfileId = searchParams.get('contractorProfileId')
    ? parseInt(searchParams.get('contractorProfileId')!)
    : undefined;

  // jobId-only mode: client arrived from job detail without a specific thread
  const jobIdFilter = urlJobId && !urlContractorProfileId ? urlJobId : undefined;

  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [filteredThreads, setFilteredThreads] = useState<ThreadSummary[]>([]);
  const [activeThread, setActiveThread] = useState<ActiveThread | undefined>(
    urlJobId && urlContractorProfileId
      ? { jobId: urlJobId, contractorProfileId: urlContractorProfileId }
      : undefined
  );
  const [messages, setMessages] = useState<ApiMessage[]>([]);
  // clientUserId for threads not yet in the list (first message scenario)
  const [newThreadClientUserId, setNewThreadClientUserId] = useState<string | undefined>(undefined);
  const [activeJob, setActiveJob] = useState<Job | null>(null);

  // Keep a ref to the latest activeThread so the SignalR callback never goes stale
  const activeThreadRef = useRef<ActiveThread | undefined>(undefined);
  useEffect(() => { activeThreadRef.current = activeThread; }, [activeThread]);


  const handleIncomingMessage = useCallback((message: ApiMessage) => {
    const current = activeThreadRef.current;
    if (current && message.jobId === current.jobId) {
      // Message belongs to the open thread - append it
      setMessages((prev) => {
        // Deduplicate: the sender already added it via REST response
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
    } else {
      // Message is for a different thread - refresh thread list to update unread count
      getConversations()
          .then((data) => { setThreads(data); setFilteredThreads(data); })
          .catch(() => {});
    }
  }, []);

  // Connect to SignalR when the user is available; disconnect on unmount
  useEffect(() => {
    if (!currentUser) return;
    startSignalRConnection(handleIncomingMessage);
    return () => { stopSignalRConnection(); };
  }, [currentUser, handleIncomingMessage]);

  // When arriving at a new thread via URL, fetch the job to get the client's userId
  useEffect(() => {
    if (!activeThread) return;
    const existing = threads.find(
      (t) => t.jobId === activeThread.jobId && t.contractorProfileId === activeThread.contractorProfileId
    );
    if (existing) { setNewThreadClientUserId(undefined); return; }
    getJobById(activeThread.jobId)
      .then((job) => setNewThreadClientUserId(job.clientUserId))
      .catch(() => setNewThreadClientUserId(undefined));
  }, [activeThread, threads]);

  // Fetch the active job so we can show the Quote panel
  useEffect(() => {
    if (!activeThread) { setActiveJob(null); return; }
    getJobById(activeThread.jobId)
      .then(setActiveJob)
      .catch(() => setActiveJob(null));
  }, [activeThread?.jobId]);

  // Fetch thread list
  useEffect(() => {
    if (!currentUser) return;
    getConversations()
      .then((data) => {
        setThreads(data);
        setFilteredThreads(data);
      })
      .catch((err) => console.error('Error fetching conversations:', err));
  }, [currentUser]);

  // Sync active thread from URL params
  useEffect(() => {
    if (urlJobId && urlContractorProfileId) {
      setActiveThread({ jobId: urlJobId, contractorProfileId: urlContractorProfileId });
    } else {
      setActiveThread(undefined);
    }
  }, [urlJobId, urlContractorProfileId]);

  // jobId-only mode: auto-navigate when threads load and there's exactly one match
  useEffect(() => {
    if (!jobIdFilter || threads.length === 0) return;
    const matches = threads.filter(t => t.jobId === jobIdFilter);
    if (matches.length === 1) {
      navigate(`/messages?jobId=${matches[0].jobId}&contractorProfileId=${matches[0].contractorProfileId}`, { replace: true });
    }
  }, [jobIdFilter, threads, navigate]);

  // Fetch messages for the active thread
  useEffect(() => {
    if (!activeThread || !currentUser) {
      setMessages([]);
      return;
    }
    getMessages(activeThread.jobId, activeThread.contractorProfileId)
      .then(setMessages)
      .catch((err) => {
        console.error('Error fetching messages:', err);
        setMessages([]);
      });
  }, [activeThread, currentUser]);

  // Accepting a Quote is the contractor-selection mechanism (ADR 0012) - refresh the Job
  // (now InProgress) and the thread list (other threads become read-only) after it fires.
  const handleQuoteAccepted = async () => {
    if (!activeThread) return;
    try {
      const updatedJob = await getJobById(activeThread.jobId);
      setActiveJob(updatedJob);
      const data = await getConversations();
      setThreads(data);
      setFilteredThreads(data);
    } catch {
      // Best-effort refresh - the Quote itself has already been accepted.
    }
  };

  const handleSendMessage = async (content: string) => {
    if (!activeThread || !currentUser) return;
    const thread = threads.find(
      (t) => t.jobId === activeThread.jobId && t.contractorProfileId === activeThread.contractorProfileId
    );
    const receiverUserId = thread?.otherUserId ?? newThreadClientUserId;
    if (!receiverUserId) return;
    try {
      const sent = await sendMessage(receiverUserId, content, activeThread.jobId);
      setMessages((prev) => [...prev, sent]);
      if (!thread) {
        getConversations().then((data) => { setThreads(data); setFilteredThreads(data); });
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleSearchThreads = (query: string) => {
    const q = query.toLowerCase();
    setFilteredThreads(
      threads.filter(
        (t) =>
          t.jobTitle.toLowerCase().includes(q) ||
          t.otherUserDisplayName.toLowerCase().includes(q)
      )
    );
  };

  const handleSelectThread = (thread: ThreadSummary) => {
    navigate(`/messages?jobId=${thread.jobId}&contractorProfileId=${thread.contractorProfileId}`);
  };

  const handleBackToList = () => {
    navigate('/messages');
    setActiveThread(undefined);
  };

  const currentThread = activeThread
    ? (threads.find(
        (t) => t.jobId === activeThread.jobId && t.contractorProfileId === activeThread.contractorProfileId
      ) ?? null)
    : null;

  const isMessageSentByUser = (message: ApiMessage) =>
    currentUser?.sub === message.senderUserId;

  return (
    <div className="bg-background text-on-background h-screen flex flex-col overflow-hidden">
      <TopAppBar />
      <main className="flex-grow min-h-0 pb-20 md:pb-0 flex flex-col md:flex-row overflow-hidden">

        {/* Thread list */}
        <div className={`${activeThread ? 'hidden md:flex' : 'flex'} w-full md:w-[380px] lg:w-96 flex-shrink-0 bg-surface border-r border-outline-variant flex-col overflow-hidden`}>
          <div className="p-4 border-b border-outline-variant">
            <h2 className="font-headline-sm text-headline-sm text-on-surface mb-3">Messages</h2>
            <div className="flex items-center gap-2 bg-surface-container-low rounded-full px-4 py-2 border border-outline-variant">
              <span className="material-symbols-outlined text-outline text-[20px]" data-icon="search">search</span>
              <input
                className="flex-grow bg-transparent border-none focus:ring-0 text-body-md placeholder:text-outline"
                placeholder="Search by job or contact..."
                type="text"
                onChange={(e) => handleSearchThreads(e.target.value)}
              />
            </div>
          </div>
          <div className="flex-grow overflow-y-auto">
            {(() => {
              const displayThreads = jobIdFilter
                ? filteredThreads.filter(t => t.jobId === jobIdFilter)
                : filteredThreads;
              if (displayThreads.length === 0) {
                return (
                  <div className="p-6 text-center text-outline text-body-md">
                    {jobIdFilter ? 'No messages about this job yet.' : 'No conversations yet.'}
                  </div>
                );
              }
              return displayThreads.map((thread) => {
                const isActive =
                  activeThread?.jobId === thread.jobId &&
                  activeThread?.contractorProfileId === thread.contractorProfileId;
                return (
                  <button
                    key={`${thread.jobId}-${thread.contractorProfileId}`}
                    onClick={() => handleSelectThread(thread)}
                    className={`w-full text-left px-4 py-3 border-b border-outline-variant hover:bg-surface-container-low transition-colors ${isActive ? 'bg-surface-container' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center flex-shrink-0 text-on-primary-container font-medium text-sm">
                        {thread.otherUserDisplayName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="flex justify-between items-baseline">
                          <span className="font-label-lg text-label-lg text-on-surface truncate">{thread.jobTitle}</span>
                          <span className="text-[11px] text-outline ml-2 flex-shrink-0">{formatTimeAgo(thread.lastMessageTimestamp)}</span>
                        </div>
                        <div className="text-body-sm text-outline truncate">{thread.otherUserDisplayName}</div>
                        <div className="text-body-sm text-outline-variant truncate mt-0.5">{thread.lastMessageContent}</div>
                        {thread.isReadOnly && (
                          <span className="text-[10px] text-outline bg-surface-container-high rounded px-1.5 py-0.5 mt-1 inline-block">Read only</span>
                        )}
                      </div>
                      {thread.unreadCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-primary text-on-primary text-[11px] flex items-center justify-center flex-shrink-0">{thread.unreadCount}</span>
                      )}
                    </div>
                  </button>
                );
              });
            })()}
          </div>
        </div>

        {/* Chat area */}
        <section className={`${activeThread ? 'flex' : 'hidden md:flex'} flex-grow flex-col bg-surface-container-lowest overflow-hidden`}>
          {currentThread ? (
            <>
              <ChatHeader
                contractorName={currentThread.otherUserDisplayName}
                isVerified={false}
                status={currentThread.isReadOnly ? 'Read only' : 'Active'}
                responseTime="< 1hr"
                specialization={currentThread.jobTitle}
                onBackClick={handleBackToList}
                isMobile={true}
                isDesktop={true}
                profileImageUrl={currentThread.otherUserProfilePictureUrl ?? ''}
              />
              {/* Quick navigation strip */}
              {activeThread && (
                <div className="px-4 py-2 bg-surface-container-low border-b border-outline-variant flex items-center gap-4 flex-shrink-0">
                  <Link
                    to={`/jobs/${activeThread.jobId}`}
                    className="flex items-center gap-1.5 text-xs font-label-bold text-secondary hover:underline"
                  >
                    <span className="material-symbols-outlined text-[15px]">work</span>
                    View job
                  </Link>
                  {!currentUser?.isContractor && (
                    <Link
                      to={`/contractors/${activeThread.contractorProfileId}`}
                      className="flex items-center gap-1.5 text-xs font-label-bold text-secondary hover:underline"
                    >
                      <span className="material-symbols-outlined text-[15px]">person</span>
                      View profile
                    </Link>
                  )}
                </div>
              )}
              <div className="flex-grow overflow-y-auto flex flex-col">
                {/* Quote panel - Contractor builds/sends a Quote; Client accepts/declines it.
                    Accepting a Quote selects the Contractor and moves the Job to InProgress (ADR 0012).
                    Lives inside this scrollable container (not fixed above it) so a long "Build your
                    Quote" form can be scrolled down to reach Save Draft / Send Quote. */}
                {activeThread && activeJob && (
                  <QuotePanel
                    jobId={activeThread.jobId}
                    contractorProfileId={activeThread.contractorProfileId}
                    jobStatus={activeJob.status}
                    isContractorSide={!!currentUser?.isContractor}
                    isClientSide={currentUser?.sub === activeJob.clientUserId}
                    onAccepted={handleQuoteAccepted}
                  />
                )}
                <div className="p-gutter space-y-6 flex flex-col flex-grow">
                  {messages.map((msg) => (
                    <ChatMessage
                      key={msg.id}
                      message={msg.content}
                      time={new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      isSent={isMessageSentByUser(msg)}
                    />
                  ))}
                </div>
              </div>
              {!currentThread.isReadOnly && (
                <MessageInput onSendMessage={handleSendMessage} isMobile={true} isDesktop={true} />
              )}
              {currentThread.isReadOnly && (
                <div className="p-4 bg-surface border-t border-outline-variant text-center text-body-sm text-outline">
                  This thread is read-only.
                </div>
              )}
            </>
          ) : activeThread ? (
            // Arrived via URL (new thread not yet in list) - still show input
            <>
              <div className="p-4 border-b border-outline-variant bg-surface flex items-center gap-3">
                <button onClick={handleBackToList} className="p-2 rounded-full hover:bg-surface-container-high md:hidden">
                  <span className="material-symbols-outlined" data-icon="arrow_back">arrow_back</span>
                </button>
                <span className="font-title-md text-on-surface">New message</span>
              </div>
              {/* Quick navigation strip */}
              <div className="px-4 py-2 bg-surface-container-low border-b border-outline-variant flex items-center gap-4 flex-shrink-0">
                <Link
                  to={`/jobs/${activeThread.jobId}`}
                  className="flex items-center gap-1.5 text-xs font-label-bold text-secondary hover:underline"
                >
                  <span className="material-symbols-outlined text-[15px]">work</span>
                  View job
                </Link>
                {!currentUser?.isContractor && (
                  <Link
                    to={`/contractors/${activeThread.contractorProfileId}`}
                    className="flex items-center gap-1.5 text-xs font-label-bold text-secondary hover:underline"
                  >
                    <span className="material-symbols-outlined text-[15px]">person</span>
                    View profile
                  </Link>
                )}
              </div>
              <div className="flex-grow overflow-y-auto p-gutter space-y-6 flex flex-col" />
              <MessageInput onSendMessage={handleSendMessage} isMobile={true} isDesktop={true} />
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-surface-bright flex-col p-10 text-center">
              <span className="material-symbols-outlined text-[64px] text-outline-variant mb-4" data-icon="chat">chat</span>
              <h2 className="font-headline-sm text-headline-sm text-primary mb-2">Select a conversation</h2>
              <p className="text-body-md text-outline max-w-[280px]">Choose a thread from the list to view messages.</p>
            </div>
          )}
        </section>
      </main>
      <BottomNavBar />
    </div>
  );
};

export default MessagesPage;
